import { Vector3 } from '@babylonjs/core';
import { EnemyDistributionConfig } from '../EnemyGenerator';
import { ENEMY_ANIMATIONS, EnemyBehavior } from './EnemyBehavior';

/**
 * Jump behavior - jump left/right on the spot
 */

export class EnemyJumpBehavior extends EnemyBehavior {
  private direction: number = 1;
  private jumpProgress: number = 0;
  private jumpDuration: number = 0.6;
  private pauseDuration: number = 0.5;
  private timer: number = 0;
  private isJumping: boolean = false;
  private lateralOffset: number = 0;
  private maxLateralOffset: number = 1;

  constructor(startPosition: Vector3, config: EnemyDistributionConfig) {
    config.sound = {
      name: 'enemyjump',
      autoplay: false,
    };
    super('enemyJump', startPosition, config);
  }

  behaviorUpdate(deltaTime: number): void {
    this.timer += deltaTime;

    if (!this.isJumping) {
      // Waiting to jump - play idle
      this.playAnimation(ENEMY_ANIMATIONS.IDLE, true);
      this.snapToGround(deltaTime); // Sanp to ground while waiting

      if (this.timer >= this.pauseDuration) {
        this.isJumping = true;
        this.timer = 0;
        this.direction *= -1;
        // Start jump animation
        this.playAnimation(ENEMY_ANIMATIONS.JUMP, false);
        this.sound.play();
      }
    } else {
      // Jumping
      this.jumpProgress = this.timer / this.jumpDuration;

      if (this.jumpProgress >= 1) {
        // Jump complete
        this.jumpProgress = 1;
        this.isJumping = false;
        this.timer = 0;
        this.lateralOffset = this.direction * this.maxLateralOffset;
        this.playAnimation(ENEMY_ANIMATIONS.IDLE, true);
      } else if (this.jumpProgress > 0.7) {
        // Falling phase
        this.playAnimation(ENEMY_ANIMATIONS.FALL, true);
      }

      // Parabolic jump arc
      const jumpArc = 4 * this.jumpProgress * (1 - this.jumpProgress);
      const yOffset = jumpArc * this.config.jumpHeight;

      // Lateral movement during jump
      const targetLateral = this.direction * this.maxLateralOffset;
      this.lateralOffset += (targetLateral - this.lateralOffset) * 0.15;

      this.mesh.position.x = this.startPosition.x + this.lateralOffset;
      this.mesh.position.z = this.startPosition.z;

      // Calculate base height (ground height) at current position
      const groundY = this.getGroundHeight() ?? this.startPosition.y;
      this.mesh.position.y = groundY + yOffset;

      // Face movement direction
      this.mesh.rotation.y = this.direction > 0 ? Math.PI / 2 : -Math.PI / 2;
    }
  }

  clone(): EnemyJumpBehavior {
    return new EnemyJumpBehavior(this.startPosition, this.config);
  }
}
